"use client";

// Suppress all framer-motion animations under the children tree until the
// KessemLoader fires its handoff. While the loader is animating, every other
// section on the page is also hydrating and firing its own initial/animate
// motion transitions — Hero's WordCascade, the CTA stagger, Magnetic's mouse
// listener, etc. On a mid-range CPU that's enough parallel work to make the
// loader's liquid-fill stutter. Setting reducedMotion="always" makes motion
// jump straight to the final state, freeing the main thread for the loader.

import { MotionConfig } from "motion/react";
import { useEffect, useState, type ReactNode } from "react";

export function MotionGate({ children }: { children: ReactNode }) {
  const [loaderDone, setLoaderDone] = useState(false);

  useEffect(() => {
    if ((window as unknown as { __kessemLoaderDone?: boolean }).__kessemLoaderDone) {
      setLoaderDone(true);
      return;
    }
    const onDone = () => setLoaderDone(true);
    window.addEventListener("kessem:loader-done", onDone);
    return () => window.removeEventListener("kessem:loader-done", onDone);
  }, []);

  // Suppress page animations during the loader so the loader's own
  // liquid-fill animation doesn't fight 12 sections' worth of
  // initial/animate motion drivers for main-thread cycles. Once the
  // loader hands off we restore "user" so animations respect the OS
  // reduced-motion setting normally. (The dev-only motion warning about
  // reduced-motion being temporarily forced is harmless and disappears
  // at handoff.)
  return (
    <MotionConfig reducedMotion={loaderDone ? "user" : "always"}>
      {children}
    </MotionConfig>
  );
}
